import React from 'react';
import { Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Beacon, COLORS } from '../types';
import styled from 'styled-components';
import { cellToLatLng } from 'h3-js';

// Create icons for different beacon types
const createBeaconIcon = (color: string, size = 12) => {
  return L.divIcon({
    className: 'beacon-marker-icon',
    html: `<div style="background-color: ${color}; width: ${size}px; height: ${size}px; border-radius: 50%; border: 2px solid white; box-shadow: 0 0 5px rgba(0,0,0,0.5);"></div>`,
    iconSize: [size + 4, size + 4],
    iconAnchor: [(size + 4) / 2, (size + 4) / 2],
    popupAnchor: [0, -(size + 4) / 2]
  });
};

// Regular beacons
const beaconIcon = createBeaconIcon(COLORS.blue);

// User's own beacon (larger and different color)
const myBeaconIcon = createBeaconIcon(COLORS.green, 16);

// Selected beacon (for measurement)
const selectedBeaconIcon = createBeaconIcon(COLORS.orange, 14);

const StyledButton = styled.button`
  padding: 4px 8px;
  background-color: ${COLORS.pink};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 5px;
  font-size: 0.9em;
`;

interface BeaconMarkerProps {
  beacon: Beacon;
  isOwnBeacon?: boolean;
  isSelected?: boolean;
  userHasBeacon?: boolean;
  isMeasuring?: boolean;
  onSelect: (beaconId: string) => void;
}

const BeaconMarker: React.FC<BeaconMarkerProps> = ({
  beacon,
  isOwnBeacon = false,
  isSelected = false,
  userHasBeacon = false,
  isMeasuring = false,
  onSelect
}) => {
  const map = useMap();

  const [lat, lng] = cellToLatLng(beacon.h3Tile);

  if (!beacon.h3Tile) {
    return null; // Don't render if beacon has no location
  }

  // Choose the appropriate icon
  const icon = isOwnBeacon ? myBeaconIcon : (isSelected ? selectedBeaconIcon : beaconIcon);

  const handleSelectClick = () => {
    onSelect(beacon.beaconId);
    // Pan to the beacon with appropriate zoom level
    // Using zoom level 8 to ensure the RTT circles will be visible
    map.flyTo([lat, lng], 8);
  };

  return (
    <Marker position={[lat, lng]} icon={icon}>
      <Popup>
        <div>
          <strong>Beacon ID:</strong> {beacon.beaconId.slice(0, 6)}...{beacon.beaconId.slice(-4)}<br />
          <strong>H3 Cell:</strong> {beacon.h3Tile}<br />
          {isOwnBeacon && <strong style={{ color: COLORS.green }}>This is your beacon</strong>}
          {!isOwnBeacon && userHasBeacon && !isMeasuring && (
            <StyledButton onClick={handleSelectClick}>
              Measure Distance
            </StyledButton>
          )}
          {!isOwnBeacon && userHasBeacon && isMeasuring && (
            <div style={{ marginTop: '5px', color: COLORS.orange }}>
              Distance measurement in progress
            </div>
          )}
          {!userHasBeacon && !isOwnBeacon && (
            <div style={{ marginTop: '5px', fontSize: '0.9em', color: '#888' }}>
              Register your own beacon to measure distance to other beacons
            </div>
          )}
        </div>
      </Popup>
    </Marker>
  );
};

export default BeaconMarker;
